import { useCallback, useMemo, useState } from 'react'
import { fromPairs, map, pipe } from 'lodash/fp'

const createCollapsedMap = pipe(
	map((id: string) => [id, false]),
	fromPairs
)
const createExpandedMap = pipe(
	map((id: string) => [id, true]),
	fromPairs
)
const DEFAULT_EXPANDED = false

export const useGroupToggle = (keys: string[]) => {
	const [state, setState] = useState<Record<string, boolean>>({})
	/**
	 * Computed values
	 */
	const isGroupExpanded = useCallback(
		(key: string) => state[key] ?? DEFAULT_EXPANDED,
		[state]
	)
	const isAllGroupsCollapsed = useMemo(
		() => (keys.length ? keys.every((key) => !isGroupExpanded(key)) : false),
		[keys, isGroupExpanded]
	)

	const collapsedMap = useMemo(() => createCollapsedMap(keys), [keys])
	const expandedMap = useMemo(() => createExpandedMap(keys), [keys])
	/**
	 * Handlers
	 */
	const toggleGroup = useCallback((id: string) => {
		setState((prev) => ({
			...prev,
			[id]: !(prev[id] ?? DEFAULT_EXPANDED),
		}))
	}, [])

	const toggleAllGroups = useCallback(() => {
		setState(isAllGroupsCollapsed ? expandedMap : collapsedMap)
	}, [isAllGroupsCollapsed, collapsedMap, expandedMap])

	return {
		isGroupExpanded,
		isAllGroupsCollapsed,
		toggleGroup,
		toggleAllGroups,
	}
}
